import React, {
  FC, useCallback, useEffect, useState,
} from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AffectedQuestions } from '../../commons/AffectedQuestions';
import { QBackButton } from '../../commons/QBackButton';
import { QConfirmButton } from '../../commons/QConfirmButton';
import { QHeader } from '../../commons/QHeader';
import { Q3Form, Q3Response } from './form';
import { Row } from 'components/base/Row';

import { useDeathLocation } from 'hooks/useDeathLocation';
import { BudgetQuestionCodes } from 'modules/budget/data/budgetQuestionCodes';
import { useBudget } from 'modules/budget/hooks/useBudget';
import { Address } from 'modules/budget/models/Address';
import { ILocation } from 'modules/budget/models/Location/types';

import { isBooleanAndFalse } from 'utils/helpers';
import { showInfoToast } from 'utils/toasts';

export const Q3: FC<Record<string, unknown>> = () => {
  const { t } = useTranslation();
  const question = BudgetQuestionCodes.Q3;
  const { RespondQuestion, budget } = useBudget();
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<ILocation | null>(null);
  const [atHome, setAtHome] = useState<boolean>();
  const [canRespond, setCanRespond] = useState<boolean>(false);
  const { getDeathLocationByCode } = useDeathLocation(selectedAddress?.city);

  useEffect(
    () => {
      const fullFilled = selectedAddress?.isFullFilled() || false;
      if (atHome) {
        setCanRespond(fullFilled);
      } else if (isBooleanAndFalse(atHome)) {
        setCanRespond(fullFilled && Boolean(selectedLocation));
      } else {
        setCanRespond(false);
      }
    },
    [atHome, selectedAddress, selectedLocation],
  );

  const response = useCallback(
    ({ gotoSummary }) => {
      if (canRespond && selectedAddress) {
        if (selectedLocation?.flowersGift) {
          showInfoToast(t('budget.flowerGiftInfo', {
            articleDescription: selectedLocation.articleDescription,
            expedient: budget.idRecord || '',
            location: getDeathLocationByCode(selectedLocation.id)?.description,
          }), {
            autoClose: false,
            position: toast.POSITION.TOP_CENTER,
          });
        }
        RespondQuestion({
          gotoSummary,
          question,
          response: {
            address: selectedAddress,
            atHome,
            location: selectedLocation,
          },
        });
      }
    },
    [
      RespondQuestion,
      atHome,
      budget.idRecord,
      canRespond, getDeathLocationByCode, question, selectedAddress, selectedLocation, t],
  );

  const onChange = useCallback(
    (responseData: Q3Response): void => {
      setSelectedAddress(responseData.address);
      setSelectedLocation(responseData.location);
      setAtHome(responseData.atHome);
    },
    [],
  );

  return (
    <React.Fragment>
      <QHeader question={question} />
      <Q3Form
        question={BudgetQuestionCodes.Q3}
        onChange={onChange}
      />
      <Row justifyContent="space-between">
        <QBackButton question={question} />
        <Row
          className="f-question-actions"
          justifyContent="flex-end"
        >
          <AffectedQuestions
            question={question}
            response={{
              address: selectedAddress,
              location: selectedLocation,
            }}
          />
          <QConfirmButton
            disabled={!canRespond}
            question={question}
            onClick={(payload) => response({ gotoSummary: payload?.gotoSummary })}
          />
        </Row>
      </Row>
    </React.Fragment>
  );
};
